import Async from "components/DesignSystem/AsyncComponents/Async";
import Modal from "components/DesignSystem/Modal/Modal";
import { DownloadIcon } from "components/icons/Download";
import { FilePdf } from "components/icons/FilePdf";
import { DISPLAY_DATE } from "constants/date";
import {
  ANNUALLY,
  MONTHLY,
  NONE,
  QUARTERLY,
  SEMI_ANNUALLY,
} from "constants/salesTaxRegistrationStatusType";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { ReactNode } from "react";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { useGetSalesTaxRegistrationDetailsQuery } from "store/apis/salesTaxRegistration";
import { BackendError } from "types/utils/error";
import { openLink } from "utils/openLink";

export const paymentScheduleMap = {
  [ANNUALLY]: "Annually",
  [MONTHLY]: "Monthly",
  [QUARTERLY]: "Quarterly",
  [SEMI_ANNUALLY]: "Semi-Annually",
  [NONE]: "",
};

const RegistrationDetailsItem = ({
  label,
  value,
  children,
}: {
  label: string;
  value?: string;
  children?: ReactNode;
}) => {
  return (
    <div>
      <p className="t-text-text-30 t-text-body-sm t-m-0 t-mb-1.5">{label}</p>
      {children || <p className="t-text-text-100 t-text-body t-m-0">{value}</p>}
    </div>
  );
};

const FileContainer = ({
  onFileClick,
  children,
}: {
  onFileClick: () => void;
  children: ReactNode;
}) => {
  return (
    <div
      onClick={onFileClick}
      className="t-flex t-items-center t-cursor-pointer t-justify-between t-border t-border-solid t-border-neutral-10 t-rounded-md t-p-2 "
    >
      <div className="t-flex t-items-center t-gap-x-2">
        <FilePdf />
        <p className="t-m-0 t-text-body t-text-text-100">{children}</p>
      </div>
      <DownloadIcon />
    </div>
  );
};

export const RegistrationDetails = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const entityId = useCurrentEntityId();
  const query = useQuery();
  const state_registration_id = query.get("state_registration_id");

  const {
    data: registrationDetailsData,
    isLoading,
    isSuccess,
    isFetching,
  } = useGetSalesTaxRegistrationDetailsQuery(
    {
      entityId,
      state_registration_id: state_registration_id as string,
    },
    { skip: !state_registration_id, refetchOnMountOrArgChange: true }
  );
  const { uuid: groupId } = useCurrentGroupContext();

  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();
  const { alertToast } = useToast();

  const handleFileClick = async ({ fileId }: { fileId: string }) => {
    try {
      const { preview_url } = await getPreviewUrl({ groupId, fileId }).unwrap();
      openLink(preview_url);
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>
            Registration details{" "}
            {Boolean(registrationDetailsData?.state && !isFetching) &&
              `(${registrationDetailsData?.state})`}
          </Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="t-max-h-[60vh] t-overflow-y-scroll">
          <Async.Root
            isLoading={isLoading || isFetching}
            isEmpty={Boolean(!registrationDetailsData?.sales_tax_id)}
            isError={!isSuccess}
            isSuccess={Boolean(
              isSuccess && registrationDetailsData && !isLoading
            )}
            loaderType="primary"
          >
            <Async.Success>
              <div className="t-flex t-gap-y-5 t-flex-col t-mb-3">
                <RegistrationDetailsItem
                  label="Sales Tax ID"
                  value={registrationDetailsData?.sales_tax_id}
                />
                <RegistrationDetailsItem
                  label="Payment schedule"
                  value={
                    registrationDetailsData?.payment_schedule &&
                    paymentScheduleMap[
                      registrationDetailsData?.payment_schedule
                    ]
                  }
                />
                <RegistrationDetailsItem
                  label="  Filing service start date"
                  value={
                    registrationDetailsData?.filing_start_date
                      ? dayjs(
                          registrationDetailsData?.filing_start_date
                        ).format(DISPLAY_DATE)
                      : ""
                  }
                />
                <RegistrationDetailsItem
                  label="Existing Tax account IDs in this state"
                  value="0123456789"
                />
                {registrationDetailsData?.application?.file_name && (
                  <RegistrationDetailsItem label="Registration application summary">
                    <FileContainer
                      onFileClick={() =>
                        handleFileClick({
                          fileId:
                            registrationDetailsData?.application?.file_id!,
                        })
                      }
                    >
                      {registrationDetailsData?.application?.file_name}
                    </FileContainer>
                  </RegistrationDetailsItem>
                )}
                {registrationDetailsData?.registration_license?.file_name && (
                  <RegistrationDetailsItem label="Sales Tax registration license">
                    <FileContainer
                      onFileClick={() =>
                        handleFileClick({
                          fileId:
                            registrationDetailsData?.registration_license
                              ?.file_id!,
                        })
                      }
                    >
                      {registrationDetailsData?.registration_license?.file_name}
                    </FileContainer>
                  </RegistrationDetailsItem>
                )}
              </div>
            </Async.Success>
          </Async.Root>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
